import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { isRole } from "../../redux/selectors/authSelector";

// Import your page components
import Board_List from "../Board-Management/Board_List";
import SchoolManagement from "../School-Management/School_List";
import TeacherManagement from "../teacher-Management/Teacher_List";
import Admissions from "../Admission/Admissions_main";
import NewsManagement from "../News-Management/NewsManagement_main";
import Education from "../Education/EducationMain";
import Department from "../Department/Department_main";
import Extracurricular from "../Extracurricular/Extracurricular_main";
import ParentList from "../Parent-Management/ParentList";
import CommunicationList from "../Communication/CommunicationList";
import { FiMoreHorizontal } from "react-icons/fi";

const HorizontalNavigationPage = () => {
  const location = useLocation();
  const schoolId = location.state?.schoolId;
  const role = useSelector(isRole);

  const mainTabFromState = location.state?.mainTab;
  const navigate = useNavigate();
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const tabContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  const countryCode =
    location.state?.countryCode ||
    localStorage.getItem("activeCountryCode") ||
    "KE";

  const boardManagementLabel =
    countryCode?.toUpperCase() === "UG"
      ? "School Management Committee"
      : "Board Management";
  const [activeTab, setActiveTab] = useState(
    mainTabFromState || boardManagementLabel,
  );

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);

    // Tabs that should NOT persist navigation state
    const noStateTabs = ["Education", "Extracurricular"];

    if (noStateTabs.includes(tabName)) {
      navigate(location.pathname, { replace: true });
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        mainTab: tabName,
        countryCode: countryCode,
      },
    });
  };

  useEffect(() => {
    if (location.state?.mainTab) {
      setActiveTab(location.state.mainTab);
    }
  }, [location.state]);

  const navigationItems = useMemo(() => [
    {
      name: "Management",
      component: <SchoolManagement schoolId={schoolId} currentTab="Management" />,
    },
    {
      name: boardManagementLabel,
      component: <Board_List schoolId={schoolId} currentTab={boardManagementLabel} />,
    },
    {
      name: "Teacher Management",
      component: <TeacherManagement schoolId={schoolId} currentTab="Teacher Management" />,
    },
    {
      name: "Admissions",
      component: <Admissions schoolId={schoolId} currentTab="Admissions" />,
    },
    {
      name: "News Management",
      component: <NewsManagement schoolId={schoolId} currentTab="News Management" />,
    },
    {
      name: "Education",
      component: <Education schoolId={schoolId} currentTab="Education" />,
    },
    {
      name: "Department",
      component: <Department schoolId={schoolId} currentTab="Department" />,
    },
    {
      name: "Extracurricular",
      component: <Extracurricular schoolId={schoolId} currentTab="Extracurricular" />,
    },
    {
      name: "Parents Management",
      component: <ParentList schoolId={schoolId} currentTab="Parents Management" />,
    },
    {
      name: "Communication",
      component: <CommunicationList schoolId={schoolId} currentTab="Communication" />,
    },
  ], [schoolId, boardManagementLabel]);
  useEffect(() => {
    const calculateTabs = () => {
      if (!tabContainerRef.current) return;

      const containerWidth = tabContainerRef.current.offsetWidth;
      const gap = 8;
      const moreButtonWidth = 48;

      let usedWidth = 0;
      const visible = [];
      const hidden = [];

      navigationItems.forEach((item) => {
        const tabWidth = item.name.length * 8 + 24;

        if (usedWidth + tabWidth + gap <= containerWidth) {
          visible.push(item);
          usedWidth += tabWidth + gap;
        } else {
          hidden.push(item);
        }
      });

      // Make room for ⋯ button
      if (hidden.length > 0) {
        while (visible.length && usedWidth + moreButtonWidth > containerWidth) {
          const removed = visible.pop();
          hidden.unshift(removed);
          usedWidth -= removed.name.length * 8 + 32 + gap;
        }
      }

      setVisibleTabs(visible);
      setHiddenTabs(hidden);
    };

    calculateTabs();
    window.addEventListener("resize", calculateTabs);

    return () => window.removeEventListener("resize", calculateTabs);
  }, [navigationItems]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    console.log("Active tab:", activeTab);
    console.log("Role:", role);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [activeTab, role]);

  // Get the active component to render
  const activeComponent = navigationItems.find(
    (item) => item.name === activeTab,
  )?.component;

  return (
    <div className="w-full h-full overflow-hidden flex flex-col ml-1">
      {/* Tab Navigation - Fixed height with scroll */}
      <div
        className="bg-primary_main rounded-xl flex-shrink-0 mt-4"
        ref={tabContainerRef}
      >
        <div className="flex gap-2 p-2 items-center">
          {/* Visible Tabs */}
          {visibleTabs.map((item) => (
            <button
              key={item.name}
              onClick={() => handleTabClick(item.name)}
              className={`px-2 py-2 rounded-md whitespace-nowrap ${activeTab === item.name
                ? "bg-white text-primary_main font-semibold"
                : "text-white hover:bg-white/10"
                }`}
            >
              {item.name}
            </button>
          ))}

          {/* ⋯ Dropdown */}
          {hiddenTabs.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`px-3 py-2 rounded-md ${hiddenTabs.some((t) => t.name === activeTab)
                  ? "bg-white text-primary_main font-semibold"
                  : "text-white hover:bg-white/10"
                  }`}
              >
                <FiMoreHorizontal size={18} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg min-w-[200px] z-50">
                  {hiddenTabs.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => handleTabClick(item.name)}
                      className={`block w-full text-left px-4 py-2 ${activeTab === item.name
                        ? "bg-primary_main text-white"
                        : "hover:bg-gray-100"
                        }`}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Content - Scrollable */}
      <div className="flex-1 overflow-y-auto mt-2 ">
        <div className="w-full h-full">{activeComponent}</div>
      </div>
    </div>
  );
};
export default HorizontalNavigationPage;
